const DEFAULT_LIMIT = 100;

type PaginationParams = {
  limit: number;
  offset: number;
};
type PaginatedFetcher<TParams, TResponse> = (params: TParams & PaginationParams) => Promise<TResponse>;

// Standard shape every paginated API in this codebase must satisfy.
type PaginatedResponse = {
  count: number;
  limit: number;
  offset: number;
  totalCount: number;
  searchParams?: Record<string, unknown>;
};

// Pick the keys of T whose value type is an array.
type ArrayKeys<T> = {
  [K in keyof T]-?: NonNullable<T[K]> extends readonly unknown[] ? K : never;
}[keyof T];

// Element type of the (single) array field of T.
type ItemOf<T> = NonNullable<T[ArrayKeys<T>]> extends readonly (infer U)[] ? U : never;

const getAllPages = async <TParams extends { limit?: number }, TResponse extends PaginatedResponse>(
  fetcher: PaginatedFetcher<TParams, TResponse>,
  params: NoInfer<TParams>,
): Promise<ItemOf<TResponse>[]> => {
  const limit = params.limit ?? DEFAULT_LIMIT;

  const fetchPage = async (offset: number) => {
    const res = await fetcher({ ...params, limit, offset });

    const page = (Object.values(res).find(Array.isArray) ?? []) as ItemOf<TResponse>[];

    return { page, totalCount: res.totalCount };
  };

  const { page: firstPage, totalCount } = await fetchPage(0);
  const items: ItemOf<TResponse>[] = firstPage;

  if (limit >= totalCount) {
    return items;
  }

  const offsets = Array.from({ length: Math.ceil(totalCount / limit) - 1 }, (_, i) => (i + 1) * limit);
  const restPages = (await Promise.all(offsets.map((o) => fetchPage(o)))).flatMap((p) => p.page);

  return [...items, ...restPages];
};

export { getAllPages };
